import type { createClient } from "redis";

/**
 * Story Version Caching — Redis-backed store for Storyblok `cv` values.
 *
 * Storyblok CDN uses `cv` (cache version) to invalidate cached stories.
 * By storing the latest `cv` per slug in Redis and passing it on every
 * request, we ensure all requests hit the Storyblok CDN (fast, cheap)
 * instead of the origin (slow, rate-limited to 50 req/s).
 *
 * On publish, the webhook deletes the slug's `cv` from Redis → next
 * fetch calls flushCache() + hits origin → gets fresh `cv` → stores it.
 *
 * https://www.storyblok.com/tp/story-version-caching
 *
 * Browser safety: redis requires Node.js TCP sockets (net, dns, tls) which
 * don't exist in browsers. The static import above is type-only (erased at
 * compile time). The runtime import is lazy with webpackIgnore so webpack
 * never bundles redis into the client bundle. All functions return null/void
 * gracefully when called in a browser context.
 */

const PREFIX = "storyblok:cv:";
const LATEST_CV_KEY = PREFIX + "__latest__";
// Bot scanners hit unique 404 URLs in bulk. Without TTL, negative-cache entries
// accumulate unbounded since flush-cache cleanup preserves the `storyblok:`
// prefix. 1h balances repeat-scan benefit vs Redis bloat.
const NEGATIVE_CACHE_TTL_SECONDS = 3600;
// Keys with TTL below this are about to expire — useful signal in monitoring.
const EXPIRING_SOON_TTL_SECONDS = 60;

let _clientPromise: Promise<ReturnType<typeof createClient> | null> | null =
  null;

async function ensureClient(): Promise<ReturnType<typeof createClient> | null> {
  if (typeof window !== "undefined") return null; // browser: Redis unavailable

  if (_clientPromise !== null) return _clientPromise;

  const url = process.env.REDIS_URL;
  if (!url) return (_clientPromise = Promise.resolve(null));

  // Assign synchronously before any await to prevent concurrent calls from
  // each creating their own Redis client (IIFE resolves to a Promise immediately).
  // webpackIgnore: redis uses Node.js-only modules (net, dns, tls).
  // This import is never reached in the browser (guarded above).
  return (_clientPromise = (async () => {
    const { createClient } = await import(/* webpackIgnore: true */ "redis");
    const useTLS = url.startsWith("rediss://");
    const client = createClient({
      url,
      ...(useTLS ? { socket: { tls: true } } : {}),
    });
    client.on("error", (err) =>
      console.error("[cv-store] Redis error:", err.message),
    );
    return client
      .connect()
      .then(() => client)
      .catch((err) => {
        console.error("[cv-store] Redis connect failed:", err.message);
        _clientPromise = null;
        return null;
      });
  })());
}

/** Returns the cached cv for a slug, or null if not set. */
export async function getCv(slug: string): Promise<number | null> {
  try {
    const client = await ensureClient();
    if (!client?.isReady) return null;
    const val = await client.get(PREFIX + slug);
    return val !== null ? Number(val) : null;
  } catch (err) {
    console.warn(
      `[cv-store] getCv("${slug}") failed:`,
      (err as Error)?.message,
    );
    return null;
  }
}

/** Stores the cv returned by Storyblok for a slug. */
export async function setCv(slug: string, cv: number): Promise<void> {
  try {
    const client = await ensureClient();
    if (!client?.isReady) return;
    await client.set(PREFIX + slug, String(cv));
  } catch (err) {
    // non-critical — next request will bypass CDN once
    console.warn(
      `[cv-store] setCv("${slug}", ${cv}) failed:`,
      (err as Error)?.message,
    );
  }
}

/**
 * Stores the cv as a negative-cache entry with a TTL. Used for slugs that
 * returned 404 from Storyblok — most are bot-scanner URLs that should not
 * persist in Redis forever.
 */
export async function setCvNegative(slug: string, cv: number): Promise<void> {
  try {
    const client = await ensureClient();
    if (!client?.isReady) return;
    await client.set(PREFIX + slug, String(cv), {
      expiration: { type: "EX", value: NEGATIVE_CACHE_TTL_SECONDS },
    });
  } catch (err) {
    console.warn(
      `[cv-store] setCvNegative("${slug}", ${cv}) failed:`,
      (err as Error)?.message,
    );
  }
}

/**
 * Deletes the cached cv for a slug.
 * Call this from the revalidate webhook so the next fetch hits Storyblok origin.
 */
export async function deleteCv(slug: string): Promise<void> {
  try {
    const client = await ensureClient();
    if (!client?.isReady) return;
    await client.del(PREFIX + slug);
  } catch (err) {
    // non-critical — stale cv just means one extra 301-bait round trip
    console.warn(
      `[cv-store] deleteCv("${slug}") failed:`,
      (err as Error)?.message,
    );
  }
}

/**
 * Latest cv seen by any successful Storyblok response. Read by the 404
 * negative-cache path: when a single-story fetch returns 404, this cv is
 * written to the per-slug key via `setCvNegative` (with TTL) so subsequent
 * hits skip the cv=0 → 301 → 404 round trip. Refreshed on every successful
 * response, never deleted by the webhook — staleness self-heals on the
 * next response.
 */
export async function getLatestCv(): Promise<number | null> {
  try {
    const client = await ensureClient();
    if (!client?.isReady) return null;
    const val = await client.get(LATEST_CV_KEY);
    return val !== null ? Number(val) : null;
  } catch (err) {
    console.warn("[cv-store] getLatestCv() failed:", (err as Error)?.message);
    return null;
  }
}

/**
 * Stores the latest cv from a successful Storyblok response — but only if
 * it advances past the current stored value. Read-then-write monotonic
 * guard: prevents an older cv (e.g. a Storyblok snapshot served at the
 * requested-cv) from overwriting a fresher `__latest__` written by a
 * parallel fetch.
 *
 * Not strictly atomic — a parallel pod could SET between our GET and SET,
 * causing a brief regression. For `__latest__` the consequence is small
 * (one suboptimal 404 negative-cache TTL window). Server-side atomicity
 * via LUA would close the race but isn't worth the complexity here.
 *
 * Recovers from corrupted state: if the stored value isn't a number,
 * `Number(...)` produces NaN, the comparison is false, and we overwrite.
 */
export async function setLatestCv(cv: number): Promise<void> {
  try {
    const client = await ensureClient();
    if (!client?.isReady) return;
    const current = await client.get(LATEST_CV_KEY);
    const currentCv = current !== null ? Number(current) : null;
    if (currentCv !== null && !Number.isNaN(currentCv) && currentCv >= cv) {
      return;
    }
    await client.set(LATEST_CV_KEY, String(cv));
  } catch (err) {
    // non-critical — next successful response will refresh on its own
    console.warn(
      `[cv-store] setLatestCv(${cv}) failed:`,
      (err as Error)?.message,
    );
  }
}

/**
 * Looks up a single cv entry. Returns key value + Redis TTL.
 * Read-only diagnostic for `/api/debug/cv-lookup`.
 *
 * `ttl` follows Redis semantics:
 *   -2 = key does not exist
 *   -1 = key exists, no TTL (persistent)
 *    N = seconds remaining before expiry
 *
 * Throws on Redis errors so callers can distinguish "key missing" (ttl=-2)
 * from "Redis unavailable".
 */
export async function lookupCv(slug: string): Promise<{
  key: string;
  cv: number | null;
  ttl: number;
}> {
  const key = PREFIX + slug;
  const client = await ensureClient();
  if (!client?.isReady) {
    throw new Error("Redis client is not ready");
  }
  const [cv, ttl] = await Promise.all([client.get(key), client.ttl(key)]);
  // node-redis v5 types client.ttl() as `number | \`${number}\``; coerce.
  return {
    key,
    cv: cv !== null ? Number(cv) : null,
    ttl: Number(ttl),
  };
}

/**
 * Counts cv keys without removing them. Uses SCAN (non-blocking) plus pipelined
 * TTL commands per batch to classify each key as persistent / expiring /
 * expiring-soon. Read-only diagnostic for `/api/debug/cv-stats`.
 *
 * `persistent` should mostly be success-path setCv() entries + __latest__.
 * `withTtl` should mostly be 404 negative-cache setCvNegative() entries.
 */
export async function countCvs(): Promise<{
  total: number;
  hasLatest: boolean;
  ttl: {
    persistent: number;
    withTtl: number;
    expiringSoon: number;
  };
}> {
  const client = await ensureClient();
  if (!client?.isReady) {
    throw new Error("Redis client is not ready");
  }

  let total = 0;
  let hasLatest = false;
  let persistent = 0;
  let withTtl = 0;
  let expiringSoon = 0;

  for await (const batch of client.scanIterator({
    MATCH: PREFIX + "*",
    COUNT: 1000,
  })) {
    if (batch.length === 0) continue;
    const multi = client.multi();
    for (const key of batch) multi.ttl(key);
    const replies = await multi.exec();

    for (let i = 0; i < batch.length; i++) {
      const key = batch[i];
      total++;
      if (key === LATEST_CV_KEY) hasLatest = true;
      // Per-command errors land in the reply slot as objects (node-redis
      // tuple-aware ReplyUnion). Narrow to number so a partial pipeline
      // failure isn't silently bucketed as `expiringSoon`.
      const ttl = replies[i];
      if (typeof ttl !== "number") continue;
      if (ttl === -1) {
        persistent++;
      } else if (ttl > 0) {
        withTtl++;
        if (ttl < EXPIRING_SOON_TTL_SECONDS) expiringSoon++;
      }
    }
  }

  return {
    total,
    hasLatest,
    ttl: { persistent, withTtl, expiringSoon },
  };
}

/**
 * Deletes ALL cached cv values.
 * Call this on deploy (flush-cache) to ensure fresh content after a new release.
 * Returns the number of keys deleted.
 */
export async function deleteAllCvs(): Promise<number> {
  try {
    const client = await ensureClient();
    if (!client?.isReady) return 0;
    const keys = await client.keys(PREFIX + "*");
    if (keys.length === 0) return 0;
    await client.del(keys);
    return keys.length;
  } catch {
    return 0;
  }
}

/**
 * Clears the __revalidated_tags__ hash from Redis.
 *
 * @fortedigital/nextjs-cache-handler stores revalidation timestamps in this hash.
 * When revalidateTag/revalidatePath is called, it writes Date.now() for each tag.
 * On get(), if any tag's timestamp > entry's lastModified (file mtime from build),
 * the entry is deleted and treated as a cache miss.
 *
 * After a fresh deploy with registerInitialCache, old __revalidated_tags__ entries
 * from previous deploys poison the cache — their timestamps are always newer than
 * the new build's file mtime, causing all seeded pages to be evicted on first request.
 *
 * Call this in flush-cache to remove stale revalidation records.
 */
export async function clearRevalidatedTags(): Promise<boolean> {
  try {
    const client = await ensureClient();
    if (!client?.isReady) return false;
    await client.del("__revalidated_tags__");
    return true;
  } catch {
    return false;
  }
}
